import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { differenceInMinutes } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import store from 'app/store';

import { logout } from 'features/auth/authSlice';

const WARNING_THRESHOLD_MINUTES = 15;
const LOGOUT_THRESHOLD_MINUTES = 20;

type IProps = {
  show: boolean;
  onSubmit?: (logout: boolean) => void;
};
/**
 * this element is meant to be destroyed completely when hidden, so we can keep local state
 */
const WarningElement: React.FC<IProps> = ({ onSubmit, show }) => {
  const { t } = useTranslation(['common']);

  const handleLogout = () => {
    if (onSubmit) {
      onSubmit(true);
    }
  };
  const handleContinue = () => {
    if (onSubmit) {
      onSubmit(false);
    }
  };

  return (
    <Dialog open={show} onClose={handleContinue}>
      <DialogTitle>{t('inactivity.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body1">{t('inactivity.message1')}</Typography>
        <Typography variant="body1">{t('inactivity.message2')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout}>{t('inactivity.logout')}</Button>
        <Button variant="contained" color="primary" onClick={handleContinue}>
          {t('inactivity.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// global tracker
let lastActive = new Date();
let idleMinutes = 0;
export function useActivityTracker() {
  const [warning, setWarning] = useState(false);

  const forceLogout = useCallback(() => {
    setWarning(false);
    store.dispatch(logout());
    window.location.href = '/login';
  }, [setWarning]);

  const handleInactive = useCallback(() => {
    const url = window.location.pathname;
    const isLoginPage = url === '/login';
    if (isLoginPage) {
      return; // no need to check inactivity on login page
    }

    const now = new Date();
    const min = differenceInMinutes(now, lastActive);
    if (min > idleMinutes) {
      idleMinutes = min;
      console.debug('inactivity check', min, 'minutes since last activity');
    }

    if (min >= LOGOUT_THRESHOLD_MINUTES) {
      console.warn('inactivity logout threshold reached, logout now');
      forceLogout();
      return;
    }

    if (warning) {
      return; // already showing warning, no need to check again
    }

    if (min >= WARNING_THRESHOLD_MINUTES) {
      // show warning
      console.warn('inactivity warning threshold reached, show warning');
      setWarning(true);
      return;
    }
  }, [setWarning, forceLogout, warning]);

  useEffect(() => {
    const updateActivity = () => {
      lastActive = new Date();
      idleMinutes = 0;
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
    };
  }, [setWarning, warning]);

  useEffect(() => {
    const interval = setInterval(handleInactive, 1000);
    return () => clearInterval(interval);
  }, [handleInactive]);

  const handleWarning = useCallback(
    (logout: boolean) => {
      if (logout) {
        forceLogout();
      }
      setWarning(false);
    },
    [forceLogout]
  );

  return <WarningElement show={warning} onSubmit={handleWarning} />;
}
